import { Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import {firstValueFrom, from} from 'rxjs';

import { PersonApiService, SwalService } from '@atmi/core';
import { toFirstUppercase } from '@atmi/core/utils';
import { RoleService } from '@sim/role';
import {
  BaseTableFilteredComponent,
  basicDeleteTemplateOptions,
  basicEditTemplateOptions,
  basicShowTemplateOptions,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { Person, PersonFilter, PersonFilterMeta } from '../../models/person';
import { PeopleFiltersComponent } from '../people-filters/people-filters.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-people',
  styleUrls: ['./people.component.scss'],
  templateUrl: './people.component.html',
})
export class PeopleComponent
  extends BaseTableFilteredComponent<Person, PersonFilter>
  implements UiTableComponent<Person> {

  readonly MobileFiltersComponent = PeopleFiltersComponent;

  readonly fields: TableField<Person>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'chekbox',
      selectable: true,
    },
    {
      context: (x) => x.nome,
      header: this.translate.instant('Nome'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'nome',
      sortable: true,
    },
    {
      context: (x) => x.cognome,
      header: this.translate.instant('Cognome'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'cognome',
      sortable: true,
    },
    {
      context: (x) => x.ragioneSociale,
      header: this.translate.instant('Rag. Sociale'),
      key: 'ragioneSociale',
    },
    {
      context: (x) => toFirstUppercase(x.tipo),
      header: this.translate.instant('Tipo'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'tipo',
      sortable: true,
    },
    {
      context: (x) => toFirstUppercase(x.categoria),
      header: this.translate.instant('Categoria'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'categoria',
      sortable: true,
    },
    {
      context: (x) => x.codiceFiscale,
      header: this.translate.instant('Cod. Fiscale'),
      key: 'codiceFiscale',
    },
    {
      context: (x) => x.partitaIva,
      header: this.translate.instant('P. IVA'),
      key: 'partitaIva',
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      template: 'context',
    },
  ];

  readonly personContextMenus: Array<TableContextMenu<Person>> = [];

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) =>
    this.personApi.index({ page, limit, sort, filter })
  );

  constructor(
    private readonly personApi: PersonApiService,
    private readonly roleService: RoleService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
  ) {
    super(translate, router, route, PersonFilterMeta, swalService);

    if (this.roleService.hasCapability('Persona', ['update'])) {
      this.personContextMenus.push({
        action: (item) => this.router.navigate(['people/manager', item.id]),
        label: this.translate.instant('Modifica'),
      });
      this.fields.push(basicEditTemplateOptions);
    } else {
      this.fields.push(basicShowTemplateOptions);
    }

    if (this.roleService.hasCapability('Persona', ['delete'])) {
      this.personContextMenus.push({
        action: (item) => item.id ? this.deleteHandler(item.id) : () => undefined,
        label: this.translate.instant('Elimina'),
      });
      this.fields.push(basicDeleteTemplateOptions);
    }
  }

  deleteConcreteHandler(id: string) {
    return this.personApi.remove(id);
  }

  deleteSelectedConcreteHandler(ids: string[]) {
    const deleteApis = ids.map((id) => firstValueFrom(this.personApi.remove(id)));

    return from(Promise.all(deleteApis));
  }
}
